import { useEffect, useState } from 'react';
import { alertsDb } from '@/lib/supabase';
import { useAuth } from '@/lib/auth';
import { useToast } from '@/components/Toast';
import { getMyTargets, savePushoverTarget, deleteTarget } from '@/services/alerts';
import type { NotificationTarget } from '@/types';

export function SettingsPage() {
  const { developer, refreshDeveloper } = useAuth();
  const toast = useToast();
  const [targets, setTargets] = useState<NotificationTarget[]>([]);
  const [userKey, setUserKey] = useState('');
  const [receivesFloor, setReceivesFloor] = useState(developer?.receives_floor ?? true);
  const [saving, setSaving] = useState(false);

  async function reload() {
    if (!developer) return;
    setTargets(await getMyTargets(developer.id));
  }

  useEffect(() => { void reload(); setReceivesFloor(developer?.receives_floor ?? true); /* eslint-disable-next-line */ }, [developer?.id]);

  async function savePushover() {
    if (!developer || !userKey.trim()) return;
    setSaving(true);
    try {
      await savePushoverTarget(developer.id, userKey.trim());
      setUserKey('');
      toast('Pushover key saved', 'success');
      await reload();
    } catch (e: any) { toast(e.message ?? 'Failed to save', 'error'); }
    finally { setSaving(false); }
  }

  async function removeTarget(id: string) {
    await deleteTarget(id);
    await reload();
  }

  async function toggleFloor(next: boolean) {
    if (!developer) return;
    setReceivesFloor(next);
    const { error } = await alertsDb().from('developers').update({ receives_floor: next, updated_at: new Date().toISOString() }).eq('id', developer.id);
    if (error) { toast(error.message, 'error'); setReceivesFloor(!next); }
    else { toast('Saved', 'success'); await refreshDeveloper(); }
  }

  return (
    <div className="page">
      <h1>Settings</h1>

      <div className="card">
        <h3>Pushover</h3>
        <p className="muted">
          Install the Pushover app, then paste your <b>User Key</b> (from the Pushover dashboard). Dev-required
          alerts arrive at emergency priority and repeat until you acknowledge them.
        </p>
        <div className="inline">
          <input className="input" value={userKey} onChange={(e) => setUserKey(e.target.value)} placeholder="Pushover user key" />
          <button className="btn btn-primary" disabled={saving || !userKey.trim()} onClick={() => void savePushover()}>Save</button>
        </div>
        <div className="list tight">
          {targets.filter((t) => t.kind === 'pushover').map((t) => (
            <div key={t.id} className="row">
              <div className="row-main">
                <div className="row-msg">Pushover {t.verified ? '· verified' : ''}</div>
                <div className="row-meta muted">key ••••{String((t.config as any).user_key ?? '').slice(-4)}{t.last_error ? ` · last error: ${t.last_error}` : ''}</div>
              </div>
              <div className="row-actions"><button className="btn btn-sm btn-danger" onClick={() => void removeTarget(t.id)}>Remove</button></div>
            </div>
          ))}
          {targets.filter((t) => t.kind === 'pushover').length === 0 && <div className="muted">No Pushover target yet — you won't get pushed until you add one.</div>}
        </div>
      </div>

      <div className="card">
        <h3>Alerting</h3>
        <label className="checkline big">
          <input type="checkbox" checked={receivesFloor} onChange={(e) => void toggleFloor(e.target.checked)} />
          Alert me for every dev-required incident (the floor)
        </label>
        <p className="muted">Turn this off to only receive incidents that match your subscriptions.</p>
      </div>

      <div className="card">
        <h3>Account</h3>
        <div className="muted">{developer?.display_name} · {developer?.email}</div>
      </div>
    </div>
  );
}
